/**
 * The video courses — `server/app/routers/courses.py`.
 *
 * The lessons are a folder on the server, so everything here is a read: the
 * catalogue, one lesson with the narration of each chapter, and the lesson's
 * WebVTT captions. The video itself is not fetched through this module — it is
 * the `src` of a `<video>` element, so the browser can seek with `Range` — and
 * {@link courseVideoUrl} only builds that address.
 *
 * The client names a lesson by its **code** (`M01`), never by a file. The server
 * decides which file that is.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import { API_BASE, api } from './client'

export interface CourseChapter {
  title: string
  /** Seconds from the start of the video. */
  start: number
  start_label: string
  /** `slides` or `live demo`. */
  kind: string
  /** Present on the one-lesson shape only. */
  narration?: string
}

export interface Lesson {
  id: string
  order: number
  title: string
  summary: string
  audience: string
  prerequisites: string[]
  /** `m:ss`, from the catalogue. The player knows the real length. */
  duration: string
  seconds: number
  has_video: boolean
  has_subtitles: boolean
  chapters: CourseChapter[]
}

export interface CourseCatalogue {
  /** `TMVERSE_COURSES_DIR` is set on the server. */
  configured: boolean
  /** The folder exists and can be read. */
  available: boolean
  product: string
  recorded_on: string
  demo_data: boolean
  lessons: Lesson[]
  /** What is wrong with the folder — empty for everyone but an administrator. */
  problems: string[]
}

export const courseKeys = {
  all: ['courses'] as const,
  one: (code: string) => ['courses', 'lesson', code] as const,
  captions: (code: string) => ['courses', 'captions', code] as const,
}

export function useCourses(): UseQueryResult<CourseCatalogue> {
  return useQuery<CourseCatalogue>({
    queryKey: courseKeys.all,
    queryFn: () => api.get<CourseCatalogue>('/api/courses'),
  })
}

/** One lesson, with each chapter's narration. Disabled until a code is given. */
export function useCourse(code: string | null): UseQueryResult<Lesson> {
  return useQuery<Lesson>({
    queryKey: courseKeys.one(code ?? ''),
    queryFn: () => api.get<Lesson>(`/api/courses/${encodeURIComponent(code ?? '')}`),
    enabled: code !== null,
  })
}

/** The captions as WebVTT text. Disabled for a lesson that has none. */
export function useCaptions(code: string | null, enabled: boolean): UseQueryResult<string> {
  return useQuery<string>({
    queryKey: courseKeys.captions(code ?? ''),
    queryFn: () => api.get<string>(`/api/courses/${encodeURIComponent(code ?? '')}/subtitles`),
    enabled: enabled && code !== null,
    // A missing caption file is an answer, not something to retry.
    retry: false,
  })
}

export function courseVideoUrl(code: string): string {
  return `${API_BASE}/api/courses/${encodeURIComponent(code)}/video`
}
